import { createSlice } from '@reduxjs/toolkit';
import { fetchRegions } from './geoThunks';
import { resolveCountryIso2 } from './geoConstants';

/**
 * Geographic catalogs shared across the platform.
 *
 * Kept outside any business module because shipping zones, order addresses and
 * order editing all read the same tree; duplicating it per feature would mean
 * refetching hundreds of communes on every screen.
 */
const initialState = {
  /** @type {Object<string, Array<{id: number, name: string, communes: Array}>>} */
  regionsByCountry: {},
  /**
   * Countries whose tree is in flight, keyed by ISO2. Tracked per country
   * rather than as one flag so the thunk can tell an in-flight request apart
   * from an idle one and skip firing a second time.
   *
   * @type {Object<string, boolean>}
   */
  loadingByCountry: {},
  error: null,
};

const geoSlice = createSlice({
  name: 'geo',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegions.pending, (state, action) => {
        state.loadingByCountry[resolveCountryIso2(action.meta.arg)] = true;
        state.error = null;
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        delete state.loadingByCountry[action.payload.countryIso2];
        state.regionsByCountry[action.payload.countryIso2] = action.payload.regions;
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        delete state.loadingByCountry[resolveCountryIso2(action.meta.arg)];
        if (action.payload) state.error = action.payload;
      });
  },
});

export const { clearError } = geoSlice.actions;

export default geoSlice.reducer;
